import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { useUpsertVoice } from '@/pages/editor/components/hooks/useUpsertVoice';
import { useAnalytics } from '@/services/analytics';
import styled from '@emotion/styled';
import { Close } from '@mui/icons-material';
import { Collapse } from '@mui/material';
import { useState } from 'react';
import { useQueryClient } from 'react-query';
import ErrorDialogContent from './ErrorDialogContent';
import LoadingDialogContent from './LoadingDialogContent';
import MainDialogContent from './MainDialogContent';
import RecordVoiceDialogContent from './RecordVoiceDialogContent';
import SuccessDialogContent from './SuccessDialogContent';
import { WizardStep } from './types';
import UploadVoiceDialogContent from './UploadVoiceDialogContent';
import { GET_VOICES_SEATS_KEY } from './useGetVoicesSeats';

const dialogStyles = {
    root: {
        borderRadius: '20px',
        padding: '35px',
        width: '732px',
    },
};

const StyledCollapse = styled(Collapse)`
    .MuiCollapse-wrapperInner {
        width: 100%;
        height: 100%;
    }
`;

const CloseButton = styled(IconButton)`
    position: absolute;
    top: 20px;
    right: 20px;
    transform: scale(1.2);
`;

const hideCloseSteps = [WizardStep.Loading];

interface Props {
    open: boolean;
    onClose: () => void;
    onBack?: () => void;
    onClone: () => Promise<void> | void;
    onSuccess?: () => void;
}

function CloneVoiceWizardModal({ open, onClose, onBack, onClone, onSuccess }: Props) {
    const [step, setStep] = useState(WizardStep.Main);
    const { mutateAsync: upsertVoice } = useUpsertVoice(undefined, true, onClone);
    const mixpanel = useAnalytics();
    const queryClient = useQueryClient();

    function handleBack() {
        setStep(WizardStep.Main);
    }

    async function handleSubmit({ file, name, language }: { file: string; name: string; language?: string }) {
        setStep(WizardStep.Loading);
        try {
            setStep(WizardStep.Loading);
            mixpanel?.track('clone-voice', { event: 'done', from: 'record' });
            await upsertVoice({ file, name, language });
            await queryClient.invalidateQueries(GET_VOICES_SEATS_KEY);

            setStep(WizardStep.Success);
        } catch (e: any) {
            if (e?.response?.data?.kind === 'PlanLimitExceededError') {
                setStep(WizardStep.LimitError);
            } else {
                setStep(WizardStep.VerificationError);
            }
        }
    }

    return (
        <BootstrapDialog open={open} rootstyle={dialogStyles.root}>
            <CloseButton onClick={onClose} ariaLabel="close" disableRipple show={!hideCloseSteps.includes(step)}>
                <Close fontSize="large" />
            </CloseButton>

            <StyledCollapse in={step === WizardStep.Main} timeout="auto" unmountOnExit>
                <MainDialogContent
                    onBack={onBack}
                    onSelect={(nextStep: WizardStep) => {
                        setStep(nextStep);
                    }}
                />
            </StyledCollapse>
            <StyledCollapse in={step === WizardStep.Upload} timeout="auto" unmountOnExit>
                <UploadVoiceDialogContent onBackClick={handleBack} onSubmit={handleSubmit} />
            </StyledCollapse>
            <StyledCollapse in={step === WizardStep.Record} timeout="auto" unmountOnExit>
                <RecordVoiceDialogContent onBackClick={handleBack} onSubmit={handleSubmit} />
            </StyledCollapse>
            <StyledCollapse in={step === WizardStep.Loading}>
                <LoadingDialogContent loadingText="Verifying..." />
            </StyledCollapse>
            <StyledCollapse in={step === WizardStep.Success}>
                <SuccessDialogContent onClose={onSuccess ?? onClone} />
            </StyledCollapse>
            <StyledCollapse in={step === WizardStep.LimitError}>
                <ErrorDialogContent onTryAgain={handleBack} title="Cloned voices limit reached" />
            </StyledCollapse>
            <StyledCollapse in={step === WizardStep.VerificationError}>
                <ErrorDialogContent
                    onTryAgain={handleBack}
                    title="Your voice was not verified"
                    subtitle="Please make sure to read out from
                    the provided script carefully."
                />
            </StyledCollapse>
        </BootstrapDialog>
    );
}

export default CloneVoiceWizardModal;
